import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getUser, type User } from '@/api/users'
import { createPayment } from '@/api/payments'
import { listPaymentTypes, type PaymentType } from '@/api/payment-types'
import { MemberSearch } from '@/components/shared/MemberSearch'
import { useActiveCashClosing } from '@/hooks/useActiveCashClosing'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import {
  Banknote, CreditCard, Building2, X, CheckCircle2,
  Loader2, Tag, ChevronLeft, UserIcon, Plus, Minus, ShoppingCart,
  LockKeyhole,
} from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface CartItem {
  key: string
  typeId: string | null
  name: string
  unitPrice: number
  qty: number
}

const METHODS = [
  { id: 'cash',     label: 'Efectivo',      icon: <Banknote   className="w-4 h-4" /> },
  { id: 'card',     label: 'Tarjeta',       icon: <CreditCard className="w-4 h-4" /> },
  { id: 'transfer', label: 'Transferencia', icon: <Building2  className="w-4 h-4" /> },
] as const

function formatCOP(n: number) {
  return `$${Number(n).toLocaleString('es-CO')}`
}

function uid() {
  return Math.random().toString(36).slice(2)
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PaymentForm() {
  const navigate          = useNavigate()
  const [searchParams]    = useSearchParams()
  const preselectedUserId = searchParams.get('userId')

  const { activeClosure, loading: cashLoading } = useActiveCashClosing()

  const [paymentTypes, setPaymentTypes]   = useState<PaymentType[]>([])
  const [linkedMember, setLinkedMember]   = useState<User | null>(null)
  const [cart, setCart]                   = useState<CartItem[]>([])
  const [methodId, setMethodId]           = useState<string>('')
  const [amountReceived, setAmountReceived] = useState<string>('')
  const [loading, setLoading]             = useState(false)

  // Custom item form
  const [customOpen, setCustomOpen]       = useState(false)
  const [customName, setCustomName]       = useState('')
  const [customPrice, setCustomPrice]     = useState('')

  useEffect(() => {
    listPaymentTypes()
      .then(r => setPaymentTypes(r.payment_types.filter((t: PaymentType) => t.is_active)))
      .catch(() => {})

    if (preselectedUserId) {
      getUser(preselectedUserId).then(r => {
        setLinkedMember(r.user)
      }).catch(() => {})
    }
  }, [preselectedUserId])

  // ── Cart helpers ────────────────────────────────────────────────────────────

  function addProduct(t: PaymentType) {
    setCart(prev => {
      const existing = prev.find(i => i.typeId === t.id)
      if (existing) {
        return prev.map(i => i.typeId === t.id ? { ...i, qty: i.qty + 1 } : i)
      }
      return [...prev, { key: uid(), typeId: t.id, name: t.name, unitPrice: Number(t.price), qty: 1 }]
    })
    setCustomOpen(false)
  }

  function addCustom() {
    const price = Number(customPrice)
    if (!customName.trim() || !price || price <= 0) {
      toast.error('Ingresa nombre y precio válidos')
      return
    }
    setCart(prev => [...prev, {
      key:       uid(),
      typeId:    null,
      name:      customName.trim(),
      unitPrice: price,
      qty:       1,
    }])
    setCustomName('')
    setCustomPrice('')
    setCustomOpen(false)
  }

  function changeQty(key: string, delta: number) {
    setCart(prev => prev
      .map(i => i.key === key ? { ...i, qty: i.qty + delta } : i)
      .filter(i => i.qty > 0)
    )
  }

  function removeItem(key: string) {
    setCart(prev => prev.filter(i => i.key !== key))
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  const total = cart.reduce((s, i) => s + i.unitPrice * i.qty, 0)

  async function handleSubmit() {
    if (cart.length === 0) { toast.error('Agrega al menos un producto'); return }
    if (!methodId) { toast.error('Selecciona un método de pago'); return }
    setLoading(true)
    try {
      // Single type when only one distinct product, null otherwise
      const distinctTypes = [...new Set(cart.map(i => i.typeId).filter(Boolean))]
      const typeId = cart.length === 1 ? cart[0].typeId ?? undefined : distinctTypes.length === 1 ? distinctTypes[0] ?? undefined : undefined
      const names  = cart.map(i => `${i.qty > 1 ? `${i.qty}x ` : ''}${i.name}`).join(', ')

      await createPayment({
        userId:          linkedMember?.id,
        clientName:      linkedMember ? undefined : names,
        paymentTypeId:   typeId,
        amount:          total,
        paymentMethodId: methodId,
      })
      toast.success('Cobro registrado')
      navigate('/payments')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al registrar cobro')
    } finally {
      setLoading(false)
    }
  }

  const cashChange      = amountReceived !== '' ? Number(amountReceived) - total : null
  const cashInsufficient = methodId === 'cash' && amountReceived !== '' && cashChange !== null && cashChange < 0
  const canSubmit = cart.length > 0 && !!methodId && !loading && !cashInsufficient
  const cartQty   = cart.reduce((s, i) => s + i.qty, 0)

  // ── Render ──────────────────────────────────────────────────────────────────

  // Gate: caja cargando
  if (cashLoading) {
    return (
      <div className="flex flex-col gap-3 h-full">
        <div className="flex items-center gap-1.5 text-text-muted text-sm">
          <ChevronLeft className="w-4 h-4" />
          <span>Cobros</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </div>
      </div>
    )
  }

  // Gate: sin caja abierta
  if (!activeClosure) {
    return (
      <div className="flex flex-col gap-3 h-full">
        <div className="flex items-center justify-between shrink-0">
          <button
            onClick={() => navigate('/payments')}
            className="flex items-center gap-1.5 text-text-muted hover:text-text-primary text-sm font-medium transition-colors cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" /> Cobros
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="flex flex-col items-center gap-4 text-center max-w-xs">
            <div className="w-14 h-14 rounded-full bg-border/50 flex items-center justify-center">
              <LockKeyhole className="w-7 h-7 text-text-muted" />
            </div>
            <div>
              <p className="text-text-primary font-semibold text-base">No hay una caja abierta</p>
              <p className="text-text-muted text-sm mt-1">
                Para registrar cobros primero debes abrir una caja de turno.
              </p>
            </div>
            <button
              onClick={() => navigate('/payments', { state: { tab: 'cierres' } })}
              className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-accent-hover transition-colors"
            >
              Ir a Cajas
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 h-full">

      {/* Breadcrumb */}
      <div className="flex items-center justify-between shrink-0">
        <button
          onClick={() => navigate('/payments')}
          className="flex items-center gap-1.5 text-text-muted hover:text-text-primary text-sm font-medium transition-colors cursor-pointer"
        >
          <ChevronLeft className="w-4 h-4" /> Cobros
        </button>
        <h1 className="text-text-primary font-bold text-base">Registrar cobro</h1>
        <div className="w-20" /> {/* spacer */}
      </div>

      {/* POS grid — flex-1 + min-h-0 para que no empuje hacia abajo */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 flex-1 min-h-0">

        {/* ══ Columna izquierda: Cliente + Productos ══ */}
        <div className="flex flex-col gap-3 min-h-0">

          {/* Cliente */}
          <div className="bg-surface border border-border rounded-2xl px-4 py-3 flex items-center gap-3 shrink-0">
            <div className="w-8 h-8 rounded-xl bg-background border border-border flex items-center justify-center shrink-0">
              <UserIcon className="w-4 h-4 text-text-muted" />
            </div>
            <div className="flex-1 min-w-0">
              <MemberSearch
                onSelect={u => setLinkedMember(u)}
                onClear={() => setLinkedMember(null)}
                value={linkedMember}
                placeholder="Buscar miembro (opcional)..."
              />
            </div>
          </div>

          {/* Productos — scroll interno */}
          <div className="bg-surface border border-border rounded-2xl flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-text-muted" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Productos y servicios</span>
              </div>
              <button
                type="button"
                onClick={() => { setCustomOpen(v => !v); setCustomName(''); setCustomPrice('') }}
                className="flex items-center gap-1 text-[11px] font-semibold text-accent hover:opacity-80 transition-opacity cursor-pointer bg-accent/8 border border-accent/20 px-2.5 py-1 rounded-lg"
              >
                <Plus className="w-3 h-3" /> Personalizado
              </button>
            </div>

            {/* Formulario monto personalizado */}
            {customOpen && (
              <div className="px-4 py-3 border-b border-border bg-accent/3 flex items-end gap-2 shrink-0">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[10px] text-text-muted font-semibold">Descripción</label>
                  <input
                    autoFocus
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCustom()}
                    placeholder="Ej: Clases de yoga"
                    className="bg-background border border-border rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent placeholder:text-text-muted/50"
                  />
                </div>
                <div className="flex flex-col gap-1 w-32">
                  <label className="text-[10px] text-text-muted font-semibold">Precio (COP)</label>
                  <input
                    type="number"
                    value={customPrice}
                    onChange={e => setCustomPrice(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCustom()}
                    placeholder="0"
                    min={1}
                    className="bg-background border border-border rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent placeholder:text-text-muted/50"
                  />
                </div>
                <button
                  type="button"
                  onClick={addCustom}
                  className="h-9 px-4 bg-accent text-background text-sm font-bold rounded-xl hover:opacity-90 transition-opacity cursor-pointer shrink-0"
                >
                  Agregar
                </button>
                <button
                  type="button"
                  onClick={() => setCustomOpen(false)}
                  className="h-9 w-9 flex items-center justify-center rounded-xl border border-border text-text-muted hover:text-text-primary transition-colors cursor-pointer shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Grid de productos */}
            <div className="overflow-y-auto flex-1 p-3">
              {paymentTypes.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 h-full py-8">
                  <Tag className="w-6 h-6 text-text-muted opacity-30" />
                  <p className="text-text-muted text-sm text-center">
                    Sin productos configurados.{' '}
                    <button onClick={() => navigate('/settings')} className="text-accent underline cursor-pointer">
                      Agregar →
                    </button>
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
                  {paymentTypes.map(t => {
                    const inCart = cart.find(i => i.typeId === t.id)
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => addProduct(t)}
                        className={`relative flex flex-col items-start gap-1.5 rounded-2xl border px-4 py-4 text-left transition-all cursor-pointer active:scale-[.97]
                          ${inCart
                            ? 'bg-accent/8 border-accent/40'
                            : 'bg-background border-border hover:border-accent/30 hover:bg-accent/4'
                          }`}
                      >
                        {inCart && (
                          <div className="absolute top-2.5 right-2.5 min-w-[20px] h-5 rounded-full bg-accent flex items-center justify-center px-1.5">
                            <span className="text-background text-[10px] font-black">{inCart.qty}</span>
                          </div>
                        )}
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${inCart ? 'bg-accent/15 border border-accent/25' : 'bg-surface border border-border'}`}>
                          <Tag className={`w-3.5 h-3.5 ${inCart ? 'text-accent' : 'text-text-muted'}`} />
                        </div>
                        <div className="pr-5 w-full">
                          <p className={`text-sm font-bold leading-tight line-clamp-2 ${inCart ? 'text-accent' : 'text-text-primary'}`}>
                            {t.name}
                          </p>
                          {t.description && (
                            <p className="text-text-muted text-[10px] mt-0.5 line-clamp-1">{t.description}</p>
                          )}
                          <p className={`text-sm font-black mt-2 tabular-nums ${inCart ? 'text-accent' : 'text-text-primary'}`}>
                            {formatCOP(Number(t.price))}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Columna derecha: Ticket POS ══ */}
        <div className="flex flex-col gap-3 min-h-0">

          {/* Ticket — scroll interno */}
          <div className="bg-surface border border-border rounded-2xl flex flex-col flex-1 min-h-0 overflow-hidden">

            {/* Header ticket */}
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-text-muted" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Ticket</span>
              </div>
              {cart.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-text-muted">{cartQty} ítem{cartQty !== 1 ? 's' : ''}</span>
                  <button
                    type="button"
                    onClick={() => setCart([])}
                    className="text-[10px] text-red-500 hover:text-red-400 font-semibold cursor-pointer"
                  >
                    Vaciar
                  </button>
                </div>
              )}
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 h-full py-8">
                  <ShoppingCart className="w-8 h-8 text-text-muted opacity-20" />
                  <p className="text-text-muted text-sm">Toca un producto para agregarlo</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {cart.map(item => (
                    <div key={item.key} className="flex items-center gap-3 px-4 py-3">
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary text-sm font-semibold leading-tight truncate">{item.name}</p>
                        <p className="text-text-muted text-[11px] mt-0.5 tabular-nums">
                          {formatCOP(item.unitPrice)} × {item.qty}
                        </p>
                      </div>

                      {/* Qty controls */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => changeQty(item.key, -1)}
                          className="w-7 h-7 rounded-lg border border-border bg-background flex items-center justify-center hover:border-red-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-text-primary text-sm font-bold tabular-nums w-5 text-center">{item.qty}</span>
                        <button
                          type="button"
                          onClick={() => changeQty(item.key, +1)}
                          className="w-7 h-7 rounded-lg border border-border bg-background flex items-center justify-center hover:border-accent hover:text-accent transition-colors cursor-pointer"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Subtotal + remove */}
                      <div className="flex flex-col items-end gap-1 shrink-0 min-w-[72px]">
                        <span className="text-text-primary text-sm font-black tabular-nums">
                          {formatCOP(item.unitPrice * item.qty)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeItem(item.key)}
                          className="text-text-muted hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Total */}
            <div className="border-t border-border px-4 py-3.5 shrink-0">
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-xs font-bold uppercase tracking-wide">Total</span>
                <span className={`text-2xl font-black tabular-nums transition-colors ${total > 0 ? 'text-text-primary' : 'text-text-muted/30'}`}>
                  {formatCOP(total)}
                </span>
              </div>
              {linkedMember && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/60">
                  <div className="w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center text-[9px] font-black text-accent shrink-0">
                    {linkedMember.name.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()}
                  </div>
                  <span className="text-text-muted text-[11px] truncate">{linkedMember.name}</span>
                  <button onClick={() => setLinkedMember(null)} className="ml-auto shrink-0 text-text-muted hover:text-red-500 transition-colors cursor-pointer">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Método de pago */}
          <div className="bg-surface border border-border rounded-2xl overflow-hidden shrink-0">
            <div className="grid grid-cols-3 divide-x divide-border">
              {METHODS.map(m => {
                const active = methodId === m.id
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setMethodId(m.id); setAmountReceived('') }}
                    className={`flex flex-col items-center gap-1.5 py-4 transition-all cursor-pointer active:scale-95
                      ${active
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        : 'bg-background text-text-muted hover:bg-accent/5 hover:text-text-primary'
                      }`}
                  >
                    {m.icon}
                    <span className="text-[10px] font-bold">{m.label}</span>
                    {active && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Panel efectivo: monto recibido + vuelto */}
          {methodId === 'cash' && (
            <div className="bg-surface border border-border rounded-2xl px-4 py-3 flex flex-col gap-2 shrink-0">
              <label className="text-[10px] font-bold uppercase tracking-widest text-text-muted">¿Con cuánto paga?</label>
              <input
                autoFocus
                type="number"
                min={0}
                value={amountReceived}
                onChange={e => setAmountReceived(e.target.value)}
                placeholder="0"
                className="bg-background border border-border rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent placeholder:text-text-muted/50 tabular-nums"
              />
              {cashChange !== null && (
                <div className={`flex items-center justify-between text-sm font-bold tabular-nums ${cashChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  <span>{cashChange >= 0 ? 'Vuelto' : 'Faltan'}</span>
                  <span>{formatCOP(Math.abs(cashChange))}</span>
                </div>
              )}
            </div>
          )}

          {/* Botón cobrar */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`w-full flex items-center justify-center gap-2.5 py-5 rounded-2xl font-black text-lg transition-all cursor-pointer active:scale-[.98] shrink-0
              ${canSubmit
                ? 'bg-accent hover:opacity-90 text-background shadow-lg shadow-accent/20'
                : 'bg-surface border border-border text-text-muted cursor-not-allowed'
              }`}
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Registrando...</>
            ) : cashInsufficient ? (
              'Monto insuficiente'
            ) : canSubmit ? (
              <><CheckCircle2 className="w-5 h-5" /> Cobrar {formatCOP(total)}</>
            ) : (
              'Agrega productos y método de pago'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
